import React, { useState, useEffect, useCallback } from 'react';
import { UnifiedHeader } from './UnifiedHeader';
import PeopleMenuBar, { PeopleTab } from './PeopleMenuBar';
import GroupsTab from './GroupsTab';
import MessagesSubPage from './MessagesSubPage';
import { useSpace } from '../contexts/SpaceContext';
import { getUnreadCount, isSignedIn } from '../services/messagingService';

interface PeopleViewProps {
  isActive: boolean;
  onNavigateToCollection: (collectionId: number) => void;
  onNavigateToInventory: (groupId: number) => void;
  onFontSizeToggle?: () => void;
  fontSize?: string;
  onOpenAppContext?: () => void;
}

const PeopleView: React.FC<PeopleViewProps> = ({
  isActive,
  onNavigateToCollection,
  onNavigateToInventory,
  onFontSizeToggle,
  fontSize,
  onOpenAppContext,
}) => {
  const { activeSpace } = useSpace();
  const [activeTab, setActiveTab] = useState<PeopleTab>(() => {
    const stored = localStorage.getItem('people-activeTab') as PeopleTab | null;
    return stored || 'messages';
  });
  const [unreadCount, setUnreadCount] = useState(0);

  const handleTabChange = (tab: PeopleTab) => {
    setActiveTab(tab);
    localStorage.setItem('people-activeTab', tab);
  };

  // Fetch unread count periodically
  const refreshUnread = useCallback(async () => {
    if (!isSignedIn()) return;
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      refreshUnread();
      const interval = setInterval(refreshUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [isActive, refreshUnread]);

  const getSubtitle = () => {
    switch (activeTab) {
      case 'messages': return 'Conversations';
      case 'groups': return 'Friends and Relations';
      default: return '';
    }
  };

  const menuBar = (
    <PeopleMenuBar
      activeTab={activeTab}
      onTabChange={handleTabChange}
      unreadCount={unreadCount}
    />
  );

  return (
    <>
      {isActive && (
        <UnifiedHeader
          title="People"
          subtitle={getSubtitle()}
          menuBar={menuBar}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
        />
      )}
      {activeTab === 'messages' && (
        <MessagesSubPage
          isActive={isActive && activeTab === 'messages'}
          onUnreadCountChange={setUnreadCount}
        />
      )}
      {activeTab === 'groups' && (
        <GroupsTab
          isActive={isActive && activeTab === 'groups'}
          onNavigateToCollection={onNavigateToCollection}
          onNavigateToInventory={onNavigateToInventory}
          showHeader={false}
        />
      )}
    </>
  );
};

export default PeopleView;
